import { Component, ElementRef, EventEmitter, HostListener, Input, Output } from '@angular/core';

// SVG <linearGradient> ids must be unique per rendered instance — a page can
// host many of these toolbars at once (e.g. one per control-measure row),
// and duplicate ids make browsers resolve url(#id) against whichever
// element happens to be first in the DOM, not necessarily this one.
let nextToolbarInstanceId = 0;

// Presentational Proofread/Rewrite/Adjust/Undo/Redo/History button row, shared across
// every AI-writing field in the app. This component owns no AI state itself —
// AiWritingFieldShellComponent drives it via inputs and listens to its outputs.
//
// density='compact' is for fields that don't have room for the full
// label+icon row (a table cell, a 1/3-width grid column): Proofread/Rewrite/Adjust
// become icon-only and Undo/Redo/History fold behind a single overflow
// button so the toolbar never grows the row/column it sits in.
@Component({
  selector: 'app-ai-writing-toolbar',
  templateUrl: './ai-writing-toolbar.component.html',
  styleUrls: ['./ai-writing-toolbar.component.scss'],
})
export class AiWritingToolbarComponent {
  @Input() proofreading = false;
  @Input() rewriting = false;
  @Input() adjusting = false;
  /** Keeps the Adjust button lit while its bar is open below the toolbar. */
  @Input() adjustOpen = false;
  @Input() hasText = false;
  @Input() hasHistory = false;
  @Input() canUndo = false;
  @Input() canRedo = false;
  @Input() density: 'full' | 'compact' = 'full';

  @Output() proofread = new EventEmitter<void>();
  @Output() rewrite = new EventEmitter<void>();
  @Output() adjust = new EventEmitter<void>();
  @Output() undo = new EventEmitter<void>();
  @Output() redo = new EventEmitter<void>();
  @Output() history = new EventEmitter<void>();

  // Compact-only: Undo/Redo/History live behind this overflow toggle instead
  // of their own always-visible icon buttons.
  overflowOpen = false;

  readonly instanceId = nextToolbarInstanceId++;

  constructor(private elementRef: ElementRef<HTMLElement>) {}

  toggleOverflow(): void {
    this.overflowOpen = !this.overflowOpen;
  }

  closeOverflow(): void {
    this.overflowOpen = false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.overflowOpen) return;
    if (!this.elementRef.nativeElement.contains(event.target as Node)) {
      this.overflowOpen = false;
    }
  }
}
